import React, {useEffect} from 'react'
import {useTable, usePagination, useRowSelect, useSortBy} from 'react-table'
import {PageWithText} from '../pagination'
import {
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiChevronsLeft,
    FiChevronsRight,
    FiChevronUp,
} from 'react-icons/fi'

const TableCheckBox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef()
        const resolvedRef = ref || defaultRef

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        return <input className="form-check" type="checkbox" ref={resolvedRef} {...rest} />
    }
)

const DatatableApiPaginator = ({
                                   columns,
                                   data,
                                   totalCount = 0,
                                   selectable = false,
                                   setSelectedRows = null,
                                   onPageSelectChange = null,
                                   pageSelectDefaultValue = 10,
                                   onPageChange = null,
                                   defaultPage = 0,
                                   sortable = false,
                                   showPagination = true
                               }) => {

    const tableInstance = useTable({
        columns,
        data,
        manualPagination: true,
        initialState: {
            pageIndex: defaultPage,
            pageSize: pageSelectDefaultValue
        }
    }, useSortBy, usePagination, useRowSelect, hooks => {
        if (selectable) {
            hooks.visibleColumns.push(columns => [
                {
                    id: 'selection',
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <TableCheckBox {...getToggleAllRowsSelectedProps()} />
                    ),
                    Cell: ({row}) => (
                        <TableCheckBox {...row.getToggleRowSelectedProps()} />
                    )
                },
                ...columns
            ])
        }
    });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        selectedFlatRows,
        state: {pageIndex, pageSize}
    } = tableInstance;

    // 🔥 pageCount se počítá AŽ TEĎ
    const pageCount = totalCount ? Math.ceil(totalCount / pageSize) : 0;

    const selectedRows = React.useMemo(() => {
        const arr = []
        if (setSelectedRows) {
            selectedFlatRows.forEach(r => arr.push(r.original.id))
            setSelectedRows(arr)
        }
        return arr
    }, [selectedFlatRows])

    useEffect(() => {
        if (onPageSelectChange) onPageSelectChange(pageSize);
        if (onPageChange) onPageChange(pageIndex, pageSize);
    }, []);

    const handlePageChange = (num) => {
        if (num >= 0 && num < pageCount) {
            gotoPage(num);
            if (onPageChange) onPageChange(num, pageSize);
        }
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        if (onPageSelectChange) onPageSelectChange(size);
        if (onPageChange) onPageChange(0, size);
    };

    return (
        <>
            <div className="bg-white">
                <div className="relative overflow-x-auto">
                    <table {...getTableProps()} className="table striped">
                        <thead>
                        {headerGroups.map(hg => (
                            <tr {...hg.getHeaderGroupProps()}>
                                {hg.headers.map(col => (
                                    <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                                        <div className="flex items-center">
                                            {col.render('Header')}
                                            {col.isSorted &&
                                                (col.isSortedDesc ? <FiChevronDown size={15}/> :
                                                    <FiChevronUp size={15}/>)
                                            }
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {page.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length}>Žádná data k zobrazení</td>
                            </tr>
                        ) : page.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            {showPagination && (
                <div className="flex items-center justify-between my-4 bg-white p-2 border rounded">

                    <div className="flex space-x-2">
                        <PageWithText active={pageIndex > 0} onClick={() => handlePageChange(0)}>
                            <FiChevronsLeft size={20}/>
                        </PageWithText>

                        <PageWithText active={(pageIndex - 1) >= 0} onClick={() => handlePageChange(pageIndex - 1)}>
                            <FiChevronLeft size={20}/>
                        </PageWithText>

                        <PageWithText active={((pageIndex+1) < (pageCount - 1))} onClick={() => handlePageChange(pageIndex + 1)}>
                            <FiChevronRight size={20}/>
                        </PageWithText>

                        <PageWithText active={pageIndex < pageCount - 1}
                                      onClick={() => handlePageChange(pageCount - 1)}>
                            <FiChevronsRight size={20}/>
                        </PageWithText>
                    </div>

                    <span>Stránka <b>{pageIndex + 1} z {pageCount}</b></span>
                    <span><b>Celkem:</b> {totalCount} záznamů</span>

                    <select value={pageSize} onChange={e => handlePageSizeChange(Number(e.target.value))}>
                        {[10, 30, 50, 100].map(size => (
                            <option key={size} value={size}>Zobrazit {size}</option>
                        ))}
                    </select>
                </div>
            )}
        </>
    );
};

export default DatatableApiPaginator;
